import { useRef, useState } from "react";
import { BookmarkEditor } from "../pages/BookmarksPage";
import { useSession } from "../session";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { PagedFooter } from "../ui/PagedFooter";
import { ErrorNote, Loading } from "../ui/States";
import { useLoad, usePaged } from "../ui/useLoad";
import { useFavorites } from "./favorites";
export function BookmarkPicker({ gid }: { gid: number }) {
  const { client } = useSession(),
    favorites = useFavorites();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLSpanElement>(null);
  const membership = useLoad(
    () => client.call("personal.bookmarkMemberships", { body: { gids: [gid] } }),
    `${gid}:${favorites.revision}`,
  );
  const ids = membership.data?.items[0]?.bookmarkIds;
  return (
    <>
      <span ref={trigger}>
        <Button aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
          {ids?.length ? `북마크 ${ids.length}개 적용됨` : "북마크"}
        </Button>
      </span>
      {open && (
        <Dialog
          label="북마크 선택"
          className="modal-small"
          onClose={() => setOpen(false)}
          returnFocus={() => trigger.current?.querySelector("button") ?? null}
        >
          <h2>북마크 선택</h2>
          <Picker gid={gid} onDone={() => setOpen(false)} />
        </Dialog>
      )}
    </>
  );
}

function Picker({ gid, onDone }: { gid: number; onDone: () => void }) {
  const { client } = useSession(),
    favorites = useFavorites();
  const [changes, setChanges] = useState(new Map<string, boolean>()),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null),
    [creating, setCreating] = useState(false);
  const list = usePaged(
    (cursor) =>
      client.call("personal.listBookmarks", {
        query: { scope: "mine", limit: 80, ...(cursor ? { cursor } : {}) },
      }),
    "owned",
  );
  const membership = useLoad(
    () => client.call("personal.bookmarkMemberships", { body: { gids: [gid] } }),
    `${gid}:${favorites.revision}`,
  );
  const original = new Set(membership.data?.items[0]?.bookmarkIds ?? []);
  async function save() {
    if (busy || membership.status !== "ready") return;
    const addIds = [...changes].filter(([id, on]) => on && !original.has(id)).map(([id]) => id),
      removeIds = [...changes].filter(([id, on]) => !on && original.has(id)).map(([id]) => id);
    if (addIds.length > 100 || removeIds.length > 100) {
      setError(new Error("한 번에 최대 100개 북마크를 변경할 수 있습니다."));
      return;
    }
    setBusy(true);
    try {
      await client.call("personal.changeBookmarkMemberships", {
        params: { gid },
        body: { addIds, removeIds },
      });
      favorites.changed(gid);
      onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="bookmark-picker-body">
      <fieldset disabled={busy || membership.status !== "ready"}>
        <legend>저장할 내 북마크</legend>
        {list.items
          .filter((b) => b.isOwner)
          .map((b) => (
            <label className="bookmark-choice" key={b.id}>
              <input
                type="checkbox"
                checked={changes.get(b.id) ?? original.has(b.id)}
                onChange={(e) => setChanges((prev) => new Map(prev).set(b.id, e.target.checked))}
              />
              {b.visibility === "public" ? "🌐 공개" : "🔒 프라이빗"} · {b.name}
            </label>
          ))}
      </fieldset>
      {membership.status === "loading" && <Loading />}
      {membership.status === "error" && <ErrorNote error={membership.error} onRetry={membership.reload} />}
      <PagedFooter list={list} emptyTitle="내 북마크가 없습니다" />
      {error !== null && <ErrorNote error={error} />}
      <div className="acts">
        <Button disabled={busy || membership.status !== "ready"} onClick={save}>
          변경 저장
        </Button>
        <Button disabled={busy} onClick={() => setCreating((v) => !v)}>
          새 북마크
        </Button>
        <Button disabled={busy} onClick={onDone}>
          취소
        </Button>
      </div>
      {creating && (
        <BookmarkEditor
          onCancel={() => setCreating(false)}
          onDone={(b) => {
            setCreating(false);
            favorites.bookmarksChanged();
            list.setItems((items) => [b, ...items]);
            setChanges((prev) => new Map(prev).set(b.id, true));
          }}
        />
      )}
    </div>
  );
}
